import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  ArrowLeft,
  User,
  Package,
  CreditCard,
  Activity,
  Calendar,
  Phone,
  Mail,
  Edit,
  Pencil,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Plus,
  FileText,
  Loader2,
  ChevronRight,
  Pause,
  Play,
  Ban,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import { formatTurkishCurrency } from "@/utils/numberUtils";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { TransferPackageFields } from "@/components/TransferPackageFields";
import { DateInputTR } from "@/components/ui/date-picker-tr";

import { MemberPendingNotice } from "@/components/MemberPendingNotice";
import { RestrictedBadge, RestrictionPanel, RecurringTemplateInfo } from "@/components/MemberRestriction";
export default function MemberDetailPage({ businessId }) {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  
  const [member, setMember] = useState(null);
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [forms, setForms] = useState([]);
  const [assignedTrainer, setAssignedTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Package categories
  const [activePackages, setActivePackages] = useState([]);
  const [completedPackages, setCompletedPackages] = useState([]);
  const [expiredPackages, setExpiredPackages] = useState([]);
  const [passivePackages, setPassivePackages] = useState([]);
  const [cancelledPackages, setCancelledPackages] = useState([]);
  const [deletedPackages, setDeletedPackages] = useState([]);
  const [frozenPackages, setFrozenPackages] = useState([]);
  const [isPrivileged, setIsPrivileged] = useState(false); // owner/admin/manager — can see deleted
  const [canTransferPackage, setCanTransferPackage] = useState(false);
  const [sellIsTransfer, setSellIsTransfer] = useState(false);
  const [sellTransferUsed, setSellTransferUsed] = useState("0");
  const [sellTransferEnd, setSellTransferEnd] = useState("");
  
  // Reservations list: filter + pagination
  const [resSearchTerm, setResSearchTerm] = useState("");
  const [resStatusFilter, setResStatusFilter] = useState("all");
  const [resPage, setResPage] = useState(1);
  const [resPageSize, setResPageSize] = useState(15);
  
  // Packages list: filter
  const [pkgStatusFilter, setPkgStatusFilter] = useState("all");
  const [pkgSearchTerm, setPkgSearchTerm] = useState("");
  
  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFullEditModal, setShowFullEditModal] = useState(false);  // Full edit modal for unregistered members
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [selectedPackageForPayment, setSelectedPackageForPayment] = useState(null);
  const [trainers, setTrainers] = useState([]);
  // Edit package dates modal
  const [editingDatesPkg, setEditingDatesPkg] = useState(null); // { mp_id, start_date, end_date, validity_days }
  const [savingDates, setSavingDates] = useState(false);
  // Edit member/package number modals
  const [editingMemberNo, setEditingMemberNo] = useState(null); // { value, original }
  const [editingPackageNo, setEditingPackageNo] = useState(null); // { mp_id, value, original }
  const [savingNumber, setSavingNumber] = useState(false);
  
  // Edit form - sadece antrenör değiştirme için
  const [selectedTrainerId, setSelectedTrainerId] = useState("");
  
  // Full edit form for unregistered members
  const [editFormData, setEditFormData] = useState({
    name: "",
    surname: "",
    phone: "",
    email: "",
    birth_date: "",
    gender: "",
    health_notes: "",
    member_card: "",
    member_cards: [],
    trainer_id: "",
  });
  const [savingFullEdit, setSavingFullEdit] = useState(false);
  
  // Date picker popover state
  const [detailBirthDateOpen, setDetailBirthDateOpen] = useState(false);
  
  // Payment form
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Sell package modal
  const [showSellPackageModal, setShowSellPackageModal] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [activePriceList, setActivePriceList] = useState(null);
  const [sellPackageId, setSellPackageId] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [sellPaidAmount, setSellPaidAmount] = useState("0");
  const [sellPaymentMethod, setSellPaymentMethod] = useState("cash");  // Ödeme yöntemi
  const [sellTrainer, setSellTrainer] = useState("");
  const [sellDiscountReason, setSellDiscountReason] = useState("");
  const [sellSaleDate, setSellSaleDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [isSelling, setIsSelling] = useState(false);
  
  useEffect(() => {
    if (memberId && businessId) {
      fetchAllData();
    }
  }, [memberId, businessId]);
  
  // === Edit package dates ===
  const openEditDates = (pkg) => {
    const def = availablePackages.find((p) => p.package_id === pkg.package_id);
    const validity_days = pkg.validity_days || def?.validity_days || 30;
    setEditingDatesPkg({
      mp_id: pkg.mp_id || pkg.member_package_id,
      start_date: pkg.start_date || "",
      end_date: pkg.end_date || "",
      validity_days,
    });
  };

  const handleEditStartDate = (newStart) => {
    if (!editingDatesPkg) return;
    let newEnd = editingDatesPkg.end_date;
    if (newStart) {
      try {
        const d = new Date(newStart);
        d.setDate(d.getDate() + (editingDatesPkg.validity_days || 30));
        newEnd = toLocalDateString(d);
      } catch {
        // keep existing
      }
    }
    setEditingDatesPkg({ ...editingDatesPkg, start_date: newStart, end_date: newEnd });
  };

  const saveEditDates = async () => {
    if (!editingDatesPkg) return;
    if (!editingDatesPkg.start_date || !editingDatesPkg.end_date) {
      toast.error(t('common:fieldsRequired', 'Tarihler zorunludur'));
      return;
    }
    if (editingDatesPkg.start_date > editingDatesPkg.end_date) {
      toast.error(t('members:startAfterEnd', 'Başlangıç tarihi bitiş tarihinden sonra olamaz'));
      return;
    }
    setSavingDates(true);
    try {
      const res = await fetch(`${API}/member-packages/${editingDatesPkg.mp_id}/dates`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start_date: editingDatesPkg.start_date,
          end_date: editingDatesPkg.end_date,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('common:operationFailed'));
      }
      toast.success(t('members:datesUpdated', 'Tarihler güncellendi'));
      setEditingDatesPkg(null);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    } finally {
      setSavingDates(false);
    }
  };

  // === Package actions: deactivate / reactivate / cancel / delete ===
  const [packageActionPkg, setPackageActionPkg] = useState(null); // { pkg, action }
  const [packageActionReason, setPackageActionReason] = useState("");
  const [packageActionLoading, setPackageActionLoading] = useState(false);
  // Cancel-specific extra state
  const [cancelRefundMode, setCancelRefundMode] = useState("free"); // 'free' | 'refund'
  const [cancelRefundAmount, setCancelRefundAmount] = useState("");
  const [cancelDebtMode, setCancelDebtMode] = useState("waive_debt"); // 'waive_debt' (Borcu Sil) | 'keep_debt' (Borç Kalsın)
  const [cancelDebtAmount, setCancelDebtAmount] = useState("");
  const [businessCards, setBusinessCards] = useState([]);
  
  // Derived info for the action dialog
  const packageActionInfo = (() => {
    if (!packageActionPkg) return null;
    const p = packageActionPkg.pkg || {};
    const paidAmount = Number(p.paid_amount || 0);
    const usedSessions = Number(p.used_sessions || 0);
    const totalSessions = Number(p.total_sessions || 0);
    const remaining = Number(p.remaining_sessions != null ? p.remaining_sessions : (totalSessions - usedSessions));
    const price = Number(p.sale_price || p.price || 0);
    const debt = Math.max(0, price - paidAmount);
    return { paidAmount, usedSessions, totalSessions, remaining, price, debt };
  })();
  
  const openPackageAction = (pkg, action) => {
    setPackageActionPkg({ pkg, action });
    setPackageActionReason("");
    setCancelRefundMode("free");
    setCancelDebtMode("waive_debt");
    const paid = Number(pkg.paid_amount || 0);
    setCancelRefundAmount(paid > 0 ? String(paid) : "");

    // Kalan borcu otomatik doldur:
    const price = Number(pkg.sale_price || pkg.price || 0);
    const debt = Math.max(0, price - paid);
    setCancelDebtAmount(debt > 0 ? String(debt) : "");
  };
  
  const closePackageAction = () => {
    setPackageActionPkg(null);
    setPackageActionReason("");
    setCancelRefundMode("free");
    setCancelDebtMode("waive_debt");
    setCancelRefundAmount("");
    setCancelDebtAmount("");
  };
  
  const submitPackageAction = async () => {
    if (!packageActionPkg) return;
    const { pkg, action } = packageActionPkg;
    const mpId = pkg.mp_id || pkg.member_package_id;
    const reason = packageActionReason.trim();
    if (!reason) {
      toast.error(t('common:reasonRequired', 'Neden zorunludur'));
      return;
    }
    
    // Cancel-specific validation: if paid_amount > 0, user must pick a refund mode + amount
    if (action === "cancel" && packageActionInfo?.paidAmount > 0) {
      if (cancelRefundMode === "refund") {
        const amt = parseFloat(cancelRefundAmount || 0);
        if (!amt || amt <= 0) {
          toast.error("İade tutarı sıfırdan büyük olmalı");
          return;
        }
        if (amt > packageActionInfo.paidAmount) {
          toast.error(`İade tutarı ödenen tutardan (${packageActionInfo.paidAmount.toFixed(0)}₺) fazla olamaz`);
          return;
        }
      }
    }
    
    setPackageActionLoading(true);
    try {
      let res;
      if (action === "deactivate") {
        res = await fetch(`${API}/member-packages/${mpId}/deactivate`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        });
      } else if (action === "cancel") {
        // Eğer borç kalsın denmişse cancel_type = "paid_cancel" gitsin ki etiket doğru bassın
        const effectiveCancelType = cancelDebtMode === "keep_debt" 
          ? "paid_cancel" 
          : (cancelRefundMode === "refund" ? "refund" : "free");

        const body = {
          reason,
          cancel_type: cancelRefundMode,
          refund_now: cancelRefundMode === "refund",
          refund_amount: cancelRefundMode === "refund" ? parseFloat(cancelRefundAmount || 0) : 0,
          debt_action: cancelDebtMode,
          debt_amount: cancelDebtMode === "keep_debt" ? parseFloat(cancelDebtAmount || 0) : 0,
        };
        res = await fetch(`${API}/member-packages/${mpId}/cancel`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } else if (action === "delete") {
        res = await fetch(`${API}/member-packages/${mpId}?reason=${encodeURIComponent(reason)}`, {
          method: "DELETE",
          credentials: "include",
        });
      }
      if (!res || !res.ok) {
        const err = res ? await res.json().catch(() => ({})) : {};
        throw new Error(err.detail || t('common:operationFailed'));
      }
      const data = await res.json().catch(() => ({}));
      let successMsg;
      if (action === "deactivate") successMsg = "Paket pasife alındı";
      else if (action === "cancel") {
        successMsg = "Paket iptal edildi";
        if (data.auto_cancelled_reservations > 0) {
          successMsg += ` (${data.auto_cancelled_reservations} açık rezervasyon otomatik iptal edildi)`;
        }
      } else successMsg = "Paket silindi";
      toast.success(successMsg);
      closePackageAction();
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    } finally {
      setPackageActionLoading(false);
    }
  };
  
  const handleReactivatePackage = async (pkg) => {
    const mpId = pkg.mp_id || pkg.member_package_id;
    try {
      const res = await fetch(`${API}/member-packages/${mpId}/reactivate`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('common:operationFailed'));
      }
      toast.success("Paket aktif edildi");
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    }
  };

  // Restore deleted package — admin/manager only, with confirmation
  const [restoreConfirm, setRestoreConfirm] = useState(null); // pkg
  
  // === EDIT SALE (price / package template) — only when no sessions used ===
  const [editSalePkg, setEditSalePkg] = useState(null);
  const [editSalePrice, setEditSalePrice] = useState("");
  const [editSalePackageId, setEditSalePackageId] = useState("");
  const [editSaleLoading, setEditSaleLoading] = useState(false);
  
  const openEditSale = (pkg) => {
    setEditSalePkg(pkg);
    setEditSalePrice(String(pkg.price || pkg.sale_price || ""));
    setEditSalePackageId(pkg.package_id || "");
  };
  
  const submitEditSale = async () => {
    if (!editSalePkg) return;
    const mpId = editSalePkg.mp_id || editSalePkg.member_package_id;
    const usedSessions = Number(editSalePkg.used_sessions || 0);
    const body = {};
    const newPrice = parseFloat(editSalePrice);

    // Validate package template change against used sessions
    if (editSalePackageId && editSalePackageId !== editSalePkg.package_id) {
      const newTpl = availablePackages.find(p => p.package_id === editSalePackageId);
      const newSessions = Number(newTpl?.session_count || 0);
      if (newSessions < usedSessions) {
        toast.error(`Bu üye ${usedSessions} ders yapmış. ${newSessions} derslik paket seçilemez.`);
        return;
      }
      body.package_id = editSalePackageId;
    }

    // Validate price range for non-privileged users
    if (!isNaN(newPrice) && newPrice > 0 && newPrice !== Number(editSalePkg.price || 0)) {
      if (!isPrivileged) {
        const pi = activePriceList?.prices?.find(p => p.package_id === editSalePackageId);
        const cands = [pi?.price, pi?.discounted_price].map(Number).filter(v => v && v > 0);
        if (cands.length) {
          const lo = Math.min(...cands), hi = Math.max(...cands);
          if (newPrice < lo || newPrice > hi) {
            toast.error(`Fiyat ${lo.toLocaleString('tr-TR')} - ${hi.toLocaleString('tr-TR')} aralığında olmalı. Aralık dışı için yönetici yetkisi gerekir.`);
            return;
          }
        }
      }
      body.price = newPrice;
    }

    if (Object.keys(body).length === 0) {
      toast.error("Değişiklik yapılmadı");
      return;
    }
    setEditSaleLoading(true);
    try {
      const res = await fetch(`${API}/member-packages/${mpId}/edit-sale`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Güncellenemedi");
      }
      toast.success("Paket güncellendi");
      setEditSalePkg(null);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || "Hata");
    } finally {
      setEditSaleLoading(false);
    }
  };

  // === FREEZE / UNFREEZE ===
  const [freezeModalPkg, setFreezeModalPkg] = useState(null);const [freezeReason, setFreezeReason] = useState("");
  const [freezeStartDate, setFreezeStartDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [freezeEndDate, setFreezeEndDate] = useState("");
  const [freezeLoading, setFreezeLoading] = useState(false);
  
  const openFreezeModal = (pkg) => {
    setFreezeModalPkg(pkg);
    setFreezeReason("");
    setFreezeStartDate(format(new Date(), "yyyy-MM-dd"));
    setFreezeEndDate("");
  };
  
  const submitFreeze = async () => {
    if (!freezeModalPkg) return;
    const reason = freezeReason.trim();
    if (!reason) {
      toast.error("Donma sebebi zorunludur");
      return;
    }
    setFreezeLoading(true);
    try {
      const mpId = freezeModalPkg.mp_id || freezeModalPkg.member_package_id;
      const res = await fetch(`${API}/member-packages/${mpId}/freeze`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason,
          freeze_start: freezeStartDate,
          freeze_end: freezeEndDate || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Donma kaydedilemedi");
      }
      const data = await res.json();
      let msg = "Paket donduruldu";
      if (data.auto_cancelled_reservations > 0) {
        msg += ` (${data.auto_cancelled_reservations} açık rezervasyon otomatik iptal edildi)`;
      }
      toast.success(msg);
      setFreezeModalPkg(null);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || "Hata oluştu");
    } finally {
      setFreezeLoading(false);
    }
  };
  
  const handleUnfreeze = async (pkg) => {
    try {
      const mpId = pkg.mp_id || pkg.member_package_id;
      const res = await fetch(`${API}/member-packages/${mpId}/unfreeze`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Devam ettirme başarısız");
      }
      const data = await res.json();
      toast.success(`Paket devam ettirildi — ${data.freeze_days} gün dondurma için bitiş tarihi uzatıldı`);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || "Hata oluştu");
    }
  };
  
  const handleRestorePackage = async (pkg) => {
    const mpId = pkg.mp_id || pkg.member_package_id;
    try {
      const res = await fetch(`${API}/member-packages/${mpId}/restore`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('common:operationFailed'));
      }
      toast.success("Paket geri yüklendi");
      setRestoreConfirm(null);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    }
  };

  // === Edit member_no / package_no manually ===
  const saveMemberNumber = async () => {
    if (!editingMemberNo) return;
    const newVal = (editingMemberNo.value || "").trim();
    if (!newVal) {
      toast.error(t('members:numberRequired', 'Numara boş olamaz'));
      return;
    }
    if (newVal === editingMemberNo.original) {
      setEditingMemberNo(null);
      return;
    }
    setSavingNumber(true);
    try {
      const res = await fetch(`${API}/members/${member.member_id}/number`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ member_no: newVal }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('common:operationFailed'));
      }
      toast.success(t('members:memberNoUpdated', 'Üye numarası güncellendi'));
      setEditingMemberNo(null);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    } finally {
      setSavingNumber(false);
    }
  };

  const savePackageNumber = async () => {
    if (!editingPackageNo) return;
    const newVal = (editingPackageNo.value || "").trim();
    if (!newVal) {
      toast.error(t('members:numberRequired', 'Numara boş olamaz'));
      return;
    }
    if (newVal === editingPackageNo.original) {
      setEditingPackageNo(null);
      return;
    }
    setSavingNumber(true);
    try {
      const res = await fetch(`${API}/member-packages/${editingPackageNo.mp_id}/number`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_no: newVal }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('common:operationFailed'));
      }
      toast.success(t('members:packageNoUpdated', 'Paket numarası güncellendi'));
      setEditingPackageNo(null);
      await fetchAllData();
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    } finally {
      setSavingNumber(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // First fetch auth/me to know privilege (owner/admin/manager)
      let myUser = null;
      try {
        const meRes = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (meRes.ok) myUser = await meRes.json();
      } catch { /* ignore */ }
      
      // Determine if privileged by checking against business
      let privileged = !!myUser?.is_admin;
      let transferOk = privileged;
      if (!privileged && myUser) {
        try {
          const bizRes = await fetch(`${API}/businesses/${businessId}`, { credentials: "include" });
          if (bizRes.ok) {
            const biz = await bizRes.json();
            if (biz?.owner_id === myUser.user_id) { privileged = true; transferOk = true; }
          }
        } catch { /* ignore */ }
        if (!privileged) {
          try {
            // Returns list of trainer records for the user
            const myTrRes = await fetch(`${API}/trainers/by-user/${myUser.user_id}`, { credentials: "include" });
            if (myTrRes.ok) {
              const trList = await myTrRes.json();
              const trHere = (Array.isArray(trList) ? trList : []).find(t => t.business_id === businessId);
              if (trHere?.is_manager) {
                privileged = true;
                transferOk = trHere?.permissions?.transfer_package_sale !== false;
              }
            }
          } catch { /* ignore */ }
        }
        if (!transferOk) {
          try {
            const myEmpRes = await fetch(`${API}/employees/by-user/${myUser.user_id}`, { credentials: "include" });
            if (myEmpRes.ok) {
              const empHere = ((await myEmpRes.json()) || []).find(e => e.business_id === businessId);
              if (empHere?.is_manager && empHere?.permissions?.transfer_package_sale !== false) transferOk = true;
            }
          } catch { /* ignore */ }
        }
      }
      setIsPrivileged(privileged);
      setCanTransferPackage(transferOk);
      
      const packagesUrl = privileged
        ? `${API}/members/${memberId}/packages?include_deleted=true`
        : `${API}/members/${memberId}/packages`;
      const [memberRes, packagesRes, paymentsRes, reservationsRes, trainersRes, availablePackagesRes, priceListRes] = await Promise.all([
        fetch(`${API}/members/${memberId}`, { credentials: "include" }),
        fetch(packagesUrl, { credentials: "include" }),
        fetch(`${API}/members/${memberId}/payments`, { credentials: "include" }),
        fetch(`${API}/members/${memberId}/reservations`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/packages`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/price-lists/active`, { credentials: "include" }),
      ]);
      
      try {
        const bizRes = await fetch(`${API}/businesses/${businessId}`, { credentials: "include" });
        if (bizRes.ok) {
          const bizData = await bizRes.json();
          // İşletmenin ayarlarındaki kabul edilen kartları state'e atar
          setBusinessCards(bizData.accepted_cards || bizData.member_cards || []);
        }
      } catch (err) {
        console.error("İşletme kartları çekilemedi:", err);
      }

      if (trainersRes.ok) {
        setTrainers(await trainersRes.json());
      }
      
      if (availablePackagesRes.ok) {
        setAvailablePackages(await availablePackagesRes.json());
      }
      
      if (priceListRes.ok) {
        setActivePriceList(await priceListRes.json());
      }
      
      if (memberRes.ok) {
        const memberData = await memberRes.json();
        setMember(memberData);
        setSelectedTrainerId(memberData.trainer_id || "none");
        
        // Fetch assigned trainer info if trainer_id exists
        if (memberData.trainer_id) {
          try {
            const trainerRes = await fetch(`${API}/trainers/${memberData.trainer_id}`, { credentials: "include" });
            if (trainerRes.ok) {
              const trainerData = await trainerRes.json();
              setAssignedTrainer(trainerData);
            }
          } catch (err) {
            console.error("Error fetching trainer:", err);
          }
        }
      }
      
      if (packagesRes.ok) {
        const packagesData = await packagesRes.json();
        setPackages(packagesData);
        categorizePackages(packagesData);
      }
      
      if (paymentsRes.ok) {
        setPayments(await paymentsRes.json());
      }
      
      if (reservationsRes.ok) {
        setReservations(await reservationsRes.json());
      }
    } catch (error) {
      console.error("Error fetching member data:", error);
      toast.error(t('common:dataLoadError'));
    } finally {
      setLoading(false);
    }
  };
  
  const categorizePackages = (pkgs) => {
    const buckets = {
      active: [], completed: [], passive: [],
      cancelled: [], deleted: [], frozen: [], expired: [],
    };
    const todayStr = todayLocalString();
    pkgs.forEach(pkg => {
      if (pkg.status === "deleted") buckets.deleted.push(pkg);
      else if (pkg.status === "frozen") buckets.frozen.push(pkg);
      else if (pkg.status === "cancelled") buckets.cancelled.push(pkg);
      else if (pkg.status === "inactive" || pkg.is_active === false) buckets.passive.push(pkg);
      else if ((pkg.remaining_sessions || 0) === 0) buckets.completed.push(pkg);
      // Süresi dolmuş ama hâlâ kullanılmamış seansı olan paketler ayrı gösterilir
      else if (pkg.end_date && String(pkg.end_date).slice(0, 10) < todayStr) buckets.expired.push(pkg);
      else buckets.active.push(pkg);
    });
    setActivePackages(buckets.active);
    setCompletedPackages(buckets.completed);
    setExpiredPackages(buckets.expired);
    setPassivePackages(buckets.passive);
    setCancelledPackages(buckets.cancelled);
    setDeletedPackages(buckets.deleted);
    setFrozenPackages(buckets.frozen);
  };
  
  const handleUpdateMember = async () => {
    try {
      // Sadece trainer_id güncellenir - diğer bilgiler üye tarafından değiştirilir
      const response = await fetch(`${API}/members/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          trainer_id: selectedTrainerId === "none" ? null : selectedTrainerId,
        }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_bagliAntrenorGuncellendi"));
        setShowEditModal(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Full edit for all members (especially unregistered)
  const handleFullUpdateMember = async () => {
    // Validate required fields for unregistered members
    if (member?.is_app_registered === false) {
      if (!editFormData.name?.trim()) {
        toast.error(t("common:toast_adAlaniZorunludur"));
        return;
      }
      if (!editFormData.surname?.trim()) {
        toast.error(t("common:toast_soyadAlaniZorunludur"));
        return;
      }
      if (!editFormData.phone?.trim()) {
        toast.error(t("common:toast_telefonAlaniZorunludur"));
        return;
      }
      if (!editFormData.birth_date) {
        toast.error(t('common:birthDateRequired'));
        return;
      }
      if (!editFormData.gender) {
        toast.error(t("common:toast_cinsiyetAlaniZorunludur"));
        return;
      }
    }
    
    setSavingFullEdit(true);
    try {
      const cleanCards = Array.isArray(editFormData.member_cards)
        ? editFormData.member_cards
        : (editFormData.member_card ? [editFormData.member_card] : []);

      const updatePayload = {
        health_notes: editFormData.health_notes || null,
        member_cards: cleanCards,
        member_card: cleanCards[0] || null,
        trainer_id: editFormData.trainer_id && editFormData.trainer_id !== "none" ? editFormData.trainer_id : null,
      };
      
      // Add personal info fields for unregistered members
      if (member?.is_app_registered === false) {
        updatePayload.name = editFormData.name;
        updatePayload.surname = editFormData.surname;
        updatePayload.phone = editFormData.phone;
        updatePayload.email = editFormData.email || null;
        updatePayload.birth_date = editFormData.birth_date || null;
        updatePayload.gender = editFormData.gender || null;
      }
      
      const response = await fetch(`${API}/members/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updatePayload),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_uyeBilgileriGuncellendi"));
        setShowFullEditModal(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingFullEdit(false);
    }
  };
  
  const handleAddPayment = async () => {
    if (!selectedPackageForPayment || !paymentAmount) {
      toast.error("Tutar gerekli");
      return;
    }
    if (savingPayment) return;
    setSavingPayment(true);

    try {
      const response = await fetch(`${API}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          member_id: memberId,
          member_package_id: selectedPackageForPayment.mp_id,
          amount: typeof paymentAmount === 'number' ? paymentAmount : parseFloat(paymentAmount),
          payment_method: paymentMethod,
          notes: paymentNotes,
          payment_date: paymentDate || null,
        }),
      });
      
      if (response.ok) {
        toast.success(t('payments:paymentSaved'));
        setShowPaymentModal(false);
        setPaymentAmount("");
        setPaymentNotes("");
        setSelectedPackageForPayment(null);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingPayment(false);
    }
  };
  
  const openPaymentModal = (pkg) => {
    setSelectedPackageForPayment(pkg);
    setPaymentAmount(pkg.debt || "");
    setPaymentNotes("");
    const d = new Date();
    setPaymentDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    setShowPaymentModal(true);
  };
  
  // Helper: Check if package has price in active price list
  const hasPackagePrice = (packageId) => {
    if (!activePriceList?.prices) return false;
    return activePriceList.prices.some(p => p.package_id === packageId);
  };
  
  // Get packages with price
  const packagesWithPrice = availablePackages
    .filter(pkg => hasPackagePrice(pkg.package_id))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", 'tr'));
  
  // Handle sell package
  const handleSellPackage = async () => {
    if (!sellPackageId) {
      toast.error(t('common:mustSelectPackage'));
      return;
    }
    
    const price = parseInt(sellPrice) || 0;
    const paidAmount = parseInt(sellPaidAmount) || 0;
    
    if (paidAmount > price) {
      toast.error(t("common:toast_odenenTutarSatisFiyatindanFazla"));
      return;
    }
    
    const sellPkgTotal = packagesWithPrice.find(p => p.package_id === sellPackageId)?.session_count || 0;
    const transferUsed = parseInt(sellTransferUsed) || 0;
    if (sellIsTransfer && (transferUsed < 0 || transferUsed >= sellPkgTotal)) {
      toast.error(`Daha önce yapılan ders sayısı 0 ile ${Math.max(0, sellPkgTotal - 1)} arasında olmalı`);
      return;
    }
    
    setIsSelling(true);
    try {
      const response = await fetch(`${API}/member-packages/sell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_id: memberId,
          package_id: sellPackageId,
          trainer_id: sellTrainer || null,
          price: price,
          paid_amount: paidAmount,
          payment_method: paidAmount > 0 && !sellIsTransfer ? sellPaymentMethod : null,  // Ödeme yöntemi
          business_id: businessId,
          discount_reason: sellDiscountReason.trim() || null,
          sale_date: sellSaleDate || null,
          is_transfer: sellIsTransfer,
          prior_used_sessions: sellIsTransfer ? transferUsed : 0,
          end_date: sellIsTransfer && sellTransferEnd ? sellTransferEnd : null,
        }),
      });
      
      if (response.ok) {
        toast.success(sellIsTransfer ? "Devir paketi kaydedildi" : t("packages:toast_paketSatisiYapildi"));
        setSellIsTransfer(false);
        setSellTransferUsed("0");
        setSellTransferEnd("");
        setShowSellPackageModal(false);
        setSellPackageId("");
        setSellTrainer("");
        setSellPrice("");
        setSellPaidAmount("0");
        setSellPaymentMethod("cash");
        setSellDiscountReason("");
        setSellSaleDate(format(new Date(), "yyyy-MM-dd"));
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:saleFailed'));
      }
    } catch (error) {
      console.error("Sell package error:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSelling(false);
    }
  };
  
  const getPackageStatusBadge = (pkg) => {
    if (pkg.status === "cancelled") {
      return <Badge variant="destructive">{t('common:cancel')}</Badge>;
    }
    if (pkg.status === "inactive" || pkg.is_active === false) {
      return <Badge variant="secondary" className="bg-slate-200 text-slate-600">{t('common:passive')}</Badge>;
    }
    if ((pkg.remaining_sessions || 0) === 0) {
      return <Badge className="bg-blue-100 text-blue-700">{t('common:completed')}</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">{t('admin:active')}</Badge>;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd MMM yyyy", { locale: getDateLocale() });
    } catch {
      return dateStr;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }
  
  if (!member) {
    return (
      <div className="text-center py-16">
        <p className="text-grey">{t("common:memberNotFound")}</p>
        <Button onClick={() => navigate("/dashboard/members")} className="mt-4">
          Üyelere Dön
        </Button>
      </div>
    );
  }
  
  // İptal edilen ama borcu korunan (keep_debt === true) paketler de borca dahil edilir:
  const activeForTotals = packages.filter(p => p.status !== "deleted" && (p.status !== "cancelled" || p.keep_debt === true));
  const totalDebt = activeForTotals.reduce((sum, p) => {
    const price = Number(p.price || p.sale_price || 0);
    const paid = Number(p.paid_amount || 0);
    return sum + Math.max(0, price - paid);
  }, 0);
  const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalSessions = activeForTotals.reduce((sum, p) => sum + (p.total_sessions || 0), 0);
  const remainingSessions = activeForTotals.reduce((sum, p) => sum + (p.remaining_sessions || 0), 0);
  
  return (
    <div className="space-y-6">
      {/* Header - Mobil uyumlu */}
      <div className="flex flex-col gap-4">
        {/* Üst satır: Geri butonu ve isim */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard/members")}
            className="rounded-xl flex-shrink-0 h-9 px-2"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('common:back')}</span>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg sm:text-2xl font-bold text-navy">
                {member.name} {member.surname}
              </h1>
              <Badge variant={member.status === "inactive" ? "secondary" : "default"} 
                     className={`text-xs ${member.status === "inactive" ? "bg-slate-200" : "bg-green-100 text-green-700"}`}>
                {member.status === "inactive" ? t('common:passive') : t('admin:active')}
              </Badge>
              <RestrictedBadge member={member} />
            </div>
            <p className="text-grey text-sm flex items-center gap-1">
              {member.member_no}
              <button
                type="button"
                onClick={() => setEditingMemberNo({ value: member.member_no || "", original: member.member_no || "" })}
                className="p-0.5 rounded hover:bg-grey-100 text-grey hover:text-navy"
                title="Üye numarasını düzenle"
                data-testid="edit-member-no-btn"
              >
                <Edit className="w-3 h-3" />
              </button>
            </p>
          </div>
        </div>
        
        {/* Alt satır: İşlem butonları - yatay kaydırılabilir */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-2 px-2">
          <Button 
            onClick={() => {
              setSellPackageId("");
              setSellPrice("");
              setSellPaidAmount("0");
              setSellTrainer("");
              setSellDiscountReason("");
              setShowSellPackageModal(true);
            }} 
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-sm h-9 px-3 flex-shrink-0"
            data-testid="sell-package-detail-btn"
          >
            <Package className="w-4 h-4 mr-1.5" />
            {t('packages:sellPackage')}
          </Button>
          <Button 
            onClick={() => {
              setEditFormData({
                name: member?.name || "",
                surname: member?.surname || "",
                phone: member?.phone || "",
                email: member?.email || "",
                birth_date: member?.birth_date || "",
                gender: member?.gender || "",
                health_notes: member?.health_notes || "",
                member_card: member?.member_card || "",
                member_cards: Array.isArray(member?.member_cards) 
                ? member.member_cards 
                : (member?.member_card && member.member_card !== "none" ? [member.member_card] : []),
                trainer_id: member?.trainer_id || "none",
              });
              setShowFullEditModal(true);
            }} 
            className="rounded-xl bg-navy text-sm h-9 px-3 flex-shrink-0"
          >
            <Edit className="w-4 h-4 mr-1.5" />
            {t('common:edit')}
          </Button>
        </div>
      </div>
      
      <MemberPendingNotice member={member} businessId={businessId} onChanged={fetchAllData}
        onSell={() => document.querySelector('[data-testid="sell-package-detail-btn"]')?.click()} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("common:toplamOdeme")}</p>
                <p className="font-bold text-lg text-green-600">{formatTurkishCurrency(totalPaid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${totalDebt > 0 ? 'bg-red-100' : 'bg-green-100'} flex items-center justify-center`}>
                <AlertCircle className={`w-5 h-5 ${totalDebt > 0 ? 'text-red-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="text-xs text-grey">{t('payments:totalDebt')}</p>
                <p className={`font-bold text-lg ${totalDebt > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatTurkishCurrency(totalDebt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("members:activePackage")}</p>
                <p className="font-bold text-lg text-blue-600">{activePackages.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t('common:remainingLessons')}</p>
                <p className="font-bold text-lg text-amber-600">{remainingSessions}/{totalSessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs - Mobilde yatay kaydırılabilir */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-slate-100 p-1 rounded-xl inline-flex min-w-max">
            <TabsTrigger value="overview" className="rounded-lg text-sm px-3">{t('common:generalInfo')}</TabsTrigger>
            <TabsTrigger value="packages" className="rounded-lg text-sm px-3">
              {t('packages:packages')} ({packages.length})
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg text-sm px-3">
              {t('common:payments')} ({payments.length})
            </TabsTrigger>
            <TabsTrigger value="reservations" className="rounded-lg text-sm px-3">
              {t('common:reservationsTab')} ({reservations.length})
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <RestrictionPanel member={member} onChanged={(m) => setMember((p) => ({ ...p, booking_restriction: m.booking_restriction }))} />
            <RecurringTemplateInfo member={member} onChanged={(m) => setMember((p) => ({ ...p, recurring_template: m.recurring_template }))} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('common:personalInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-grey">{t('common:fullName')}</span>
                  <span className="font-medium">{member.name} {member.surname}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey">{t("common:uyeNo")}</span>
                  <code className="bg-slate-100 px-2 py-0.5 rounded">{member.member_no}</code>
                </div>
                {/* Bağlı Antrenör - trainers listesinden bul */}
                {member.trainer_id && (
                  <div className="flex justify-between">
                    <span className="text-grey">{t("common:assignedTrainer")}</span>
                    <Badge className="bg-purple-100 text-purple-700">
                      {(() => {
                        const trainer = trainers.find(t => t.trainer_id === member.trainer_id);
                        return trainer ? `${trainer.name} ${trainer.surname}` : assignedTrainer ? `${assignedTrainer.name} ${assignedTrainer.surname}` : t("common:loadingDots");
                      })()}
                    </Badge>
                  </div>
                )}
                {member.phone && (
                  <div className="flex justify-between">
                    <span className="text-grey">{t('common:phone')}</span>
                    <span className="font-medium">{member.phone}</span>
                  </div>
                )}
                {member.email && (
                  <div className="flex justify-between">
                    <span className="text-grey">{t('common:email')}</span>
                    <span className="font-medium">{member.email}</span>
                  </div>
                )}
                {member.birth_date && (
                  <div className="flex justify-between">
                    <span className="text-grey">{t('members:birthDate')}</span>
                    <span className="font-medium">{formatDate(member.birth_date)}</span>
                  </div>
                )}
                {member.gender && (
                  <div className="flex justify-between">
                    <span className="text-grey">{t('common:gender')}</span>
                    <span className="font-medium">
                      {member.gender === "male" ? t('members:male') : member.gender === "female" ? t('members:female') : t('common:notSpecified')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {t('common:registrationInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-grey">{t('members:registrationDate')}</span>
                  <span className="font-medium">{formatDate(member.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey">{t('common:status')}</span>
                  <Badge variant={member.status === "inactive" ? "secondary" : "default"}
                         className={member.status === "inactive" ? "bg-slate-200" : "bg-green-100 text-green-700"}>
                    {member.status === "inactive" ? t('common:passive') : t('admin:active')}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-grey">{t("common:uyeKarti")}</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {Array.isArray(member.member_cards) && member.member_cards.length > 0 ? (
                      member.member_cards.map((c) => (
                        <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                      ))
                    ) : member.member_card ? (
                      <Badge variant="outline" className="text-xs">{member.member_card}</Badge>
                    ) : (
                      <span className="text-sm font-medium text-grey">-</span>
                    )}
                  </div>
                </div>
                {member.notes && (
                  <div className="mt-4">
                    <span className="text-grey text-sm">{t('common:notes')}</span>
                    <p className="mt-1 text-sm bg-slate-50 p-3 rounded-lg">{member.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Packages Tab */}
        <TabsContent value="packages" className="mt-6 space-y-6">
          {/* Active Packages */}
          {activePackages.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  {t('packages:ongoingPackages')} ({activePackages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {activePackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  const paidAmount = pkg.paid_amount || 0;
                  const remaining = Math.max(0, pkgPrice - paidAmount);
                  
                  return (
                    <div 
                      key={pkg.mp_id} 
                      className="p-4 rounded-xl border bg-green-50 border-green-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          {(pkg.package_no || pkg.mp_id) && (
                            <span className="inline-flex items-center gap-1 font-mono text-xs text-grey-700 bg-white border border-grey-200 px-1.5 py-0.5 rounded">
                              {pkg.package_no || "-"}
                              <button
                                type="button"
                                onClick={() => setEditingPackageNo({ mp_id: pkg.mp_id || pkg.member_package_id, value: pkg.package_no || "", original: pkg.package_no || "" })}
                                className="p-0.5 rounded hover:bg-grey-100 text-grey hover:text-navy"
                                title="Paket numarasını düzenle"
                                data-testid={`edit-package-no-${pkg.mp_id || pkg.member_package_id}`}
                              >
                                <Edit className="w-2.5 h-2.5" />
                              </button>
                            </span>
                          )}
                          <Badge className="bg-green-100 text-green-700 text-xs">{t('admin:active')}</Badge>
                          {pkg.is_transfer && (
                            <Badge className="bg-violet-100 text-violet-700 text-xs" title={`Sistem öncesi ${pkg.transfer_used_sessions || 0} ders yapılmış`} data-testid={`transfer-badge-${pkg.mp_id || pkg.member_package_id}`}>Devir</Badge>
                          )}
                          {remaining > 0 && (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              {formatTurkishCurrency(remaining)} {t('common:debt')}
                            </Badge>
                          )}
                        </div>
                        {remaining > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPaymentModal(pkg)}
                            className="rounded-lg"
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            {t('common:collect')}
                          </Button>
                        )}
                      </div>
                      
                      {/* Action buttons row */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => openEditSale(pkg)}
                          data-testid={`pkg-edit-${pkg.mp_id || pkg.member_package_id}`}
                          title="Düzenle (fiyat / paket)"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          onClick={() => openFreezeModal(pkg)}
                          data-testid={`pkg-freeze-${pkg.mp_id || pkg.member_package_id}`}
                          title="Dondur"
                        >
                          <Pause className="w-3 h-3 mr-1" />
                          Dondur
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
                          onClick={() => openPackageAction(pkg, "deactivate")}
                          data-testid={`pkg-deactivate-${pkg.mp_id || pkg.member_package_id}`}
                          title="Pasife Al"
                        >
                          <Pause className="w-3 h-3 mr-1" />
                          Pasife Al
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                          onClick={() => openPackageAction(pkg, "cancel")}
                          data-testid={`pkg-cancel-${pkg.mp_id || pkg.member_package_id}`}
                          title="İptal Et"
                        >
                          <Ban className="w-3 h-3 mr-1" />
                          İptal Et
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => openPackageAction(pkg, "delete")}
                          data-testid={`pkg-delete-${pkg.mp_id || pkg.member_package_id}`}
                          title="Sil"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Sil
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-grey text-xs mb-1">Yapılan / Kalan / Toplam</p>
                          <p className="font-bold">
                            <span className="text-orange-600">{pkg.used_sessions || 0}</span>
                            <span className="text-grey"> / </span>
                            <span className="text-green-600">{pkg.remaining_sessions}</span>
                            <span className="text-grey"> / {pkg.total_sessions}</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('common:price')}</p>
                          <p className="font-bold">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1 flex items-center gap-1">
                            {t('settings:startTime')}
                            <button
                              type="button"
                              onClick={() => openEditDates(pkg)}
                              className="ml-1 p-0.5 rounded hover:bg-white/60 text-grey hover:text-navy"
                              title="Tarihleri düzenle"
                              data-testid={`edit-dates-active-${pkg.mp_id || pkg.member_package_id}`}
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                          </p>
                          <p className="font-medium">{formatDate(pkg.start_date || pkg.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:endTime')}</p>
                          <p className="font-medium">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</p>
                        </div>
                      </div>
                      
                      {/* Ödeme Durumu */}
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-grey">{t('common:paymentStatusLabel')}</span>
                          <span className="font-medium">
                            {formatTurkishCurrency(paidAmount)} {t('common:paidLabel')}
                            {remaining > 0 && (
                              <span className="text-red-600 ml-2">
                                ({formatTurkishCurrency(remaining)} {t('common:leftLabel')})
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
          
          {/* Completed Packages */}
          {completedPackages.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                  <CheckCircle2 className="w-5 h-5" />
                  {t('packages:completedPackages')} ({completedPackages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {completedPackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  const paidAmount = pkg.paid_amount || 0;
                  const remaining = Math.max(0, pkgPrice - paidAmount);
                  
                  return (
                    <div 
                      key={pkg.mp_id} 
                      className="p-4 rounded-xl border bg-blue-50 border-blue-200 opacity-80"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          {pkg.package_no && (
                            <span className="font-mono text-xs text-grey-700 bg-white border border-grey-200 px-1.5 py-0.5 rounded">
                              {pkg.package_no}
                            </span>
                          )}
                          <Badge className="bg-blue-100 text-blue-700 text-xs">{t('common:completed')}</Badge>
                          {remaining > 0 && (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              {formatTurkishCurrency(remaining)} {t('common:debt')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-grey text-xs mb-1">{t("common:kullanilan")}</p>
                          <p className="font-bold">{pkg.total_sessions} / {pkg.total_sessions}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('common:price')}</p>
                          <p className="font-bold">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:startTime')}</p>
                          <p className="font-medium">{formatDate(pkg.start_date || pkg.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:endTime')}</p>
                          <p className="font-medium">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Expired Packages (date passed, still have unused sessions) */}
          {expiredPackages.length > 0 && (
            <Card className="border-0 shadow-sm" data-testid="expired-packages-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <AlertCircle className="w-5 h-5" />
                  Süresi Dolmuş Paketler ({expiredPackages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {expiredPackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  return (
                    <div
                      key={pkg.mp_id}
                      className="p-4 rounded-xl border bg-amber-50 border-amber-200"
                      data-testid={`expired-package-${pkg.mp_id || pkg.member_package_id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          {pkg.package_no && (
                            <span className="font-mono text-xs text-grey-700 bg-white border border-grey-200 px-1.5 py-0.5 rounded">
                              {pkg.package_no}
                            </span>
                          )}
                          <Badge className="bg-amber-100 text-amber-800 text-xs">Süresi Doldu</Badge>
                        </div>
                      </div>
                      <p className="text-xs text-amber-800 mb-3">
                        Bu paketin geçerlilik süresi <span className="font-semibold">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</span> tarihinde doldu. {pkg.remaining_sessions} kullanılmamış ders var ancak rezervasyon yapılamaz. Süreyi uzatmak için aşağıdaki butonu kullanabilirsiniz.
                      </p>
                      <div className="mb-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                          onClick={() => openEditDates(pkg)}
                          data-testid={`expired-extend-${pkg.mp_id || pkg.member_package_id}`}
                        >
                          <Calendar className="w-3 h-3 mr-1" />
                          Bitiş Tarihini Uzat
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-grey text-xs mb-1">Yapılan / Kalan / Toplam</p>
                          <p className="font-bold">{pkg.used_sessions || 0} / {pkg.remaining_sessions} / {pkg.total_sessions}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('common:price')}</p>
                          <p className="font-bold">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:startTime')}</p>
                          <p className="font-medium">{formatDate(pkg.start_date || pkg.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:endTime')}</p>
                          <p className="font-medium text-amber-700">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          
          {/* Passive Packages */}
          {passivePackages.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-500">
                  <Clock className="w-5 h-5" />
                  {t('packages:passivePackages')} ({passivePackages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {passivePackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  const paidAmount = pkg.paid_amount || 0;
                  const remaining = Math.max(0, pkgPrice - paidAmount);
                  
                  return (
                    <div 
                      key={pkg.mp_id} 
                      className="p-4 rounded-xl border bg-slate-100 border-slate-200 opacity-70"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          <Badge className="bg-slate-200 text-slate-600 text-xs">{t('common:passive')}</Badge>
                          {remaining > 0 && (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              {formatTurkishCurrency(remaining)} {t('common:debt')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Action buttons row */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleReactivatePackage(pkg)}
                          data-testid={`pkg-reactivate-${pkg.mp_id || pkg.member_package_id}`}
                          title="Aktife Al"
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Aktife Al
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => openPackageAction(pkg, "delete")}
                          data-testid={`pkg-delete-passive-${pkg.mp_id || pkg.member_package_id}`}
                          title="Sil"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Sil
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-grey text-xs mb-1">{t('packages:remainingSlashTotal')}</p>
                          <p className="font-bold">{pkg.remaining_sessions} / {pkg.total_sessions}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('common:price')}</p>
                          <p className="font-bold">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:startTime')}</p>
                          <p className="font-medium">{formatDate(pkg.start_date || pkg.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:endTime')}</p>
                          <p className="font-medium">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</p>
                        </div>
                      </div>
                      
                      {pkg.passive_reason && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-grey">{t('common:passivateDueToReason')}</p>
                          <p className="text-sm">{pkg.passive_reason}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
          
          {/* Cancelled Packages */}
          {cancelledPackages.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                  <XCircle className="w-5 h-5" />
                  {t('packages:cancelledPackages')} ({cancelledPackages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {cancelledPackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  const isFreeCancel = pkg.cancel_type === 'free';
                  
                  return (
                    <div 
                      key={pkg.mp_id} 
                      className="p-4 rounded-xl border bg-red-50 border-red-200 opacity-60"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            {isFreeCancel ? t('common:freeCancellation') : t('common:cancellationWithRefund')}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 rounded-lg text-xs border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => openPackageAction(pkg, "delete")}
                          data-testid={`pkg-delete-cancelled-${pkg.mp_id || pkg.member_package_id}`}
                          title="Sil"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Sil
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-grey text-xs mb-1">{t('common:price')}</p>
                          <p className="font-bold">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t("common:i̇ptalTarihi")}</p>
                          <p className="font-medium">{formatDate(pkg.cancelled_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:startTime')}</p>
                          <p className="font-medium">{formatDate(pkg.start_date || pkg.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey text-xs mb-1">{t('settings:endTime')}</p>
                          <p className="font-medium">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</p>
                        </div>
                      </div>
                      
                      {pkg.cancel_reason && (
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <p className="text-xs text-grey">{t('common:cancellationReason')}:</p>
                          <p className="text-sm">{pkg.cancel_reason}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
          
          {/* Frozen (dondurulmuş) Packages */}
          {frozenPackages.length > 0 && (
            <Card className="border-0 shadow-sm border-l-4 border-l-cyan-400">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-cyan-700">
                  <Pause className="w-5 h-5" />
                  Dondurulmuş Paketler ({frozenPackages.length})
                </CardTitle>
                <p className="text-xs text-grey mt-1">Donma süresi bitiş tarihine eklenir. Bu paket için rezervasyon oluşturulamaz.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {frozenPackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  return (
                    <div
                      key={pkg.mp_id}
                      className="p-3 rounded-xl border border-cyan-200 bg-cyan-50"
                      data-testid={`frozen-pkg-${pkg.mp_id}`}
                    >
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          {pkg.package_no && <Badge variant="outline" className="text-xs">{pkg.package_no}</Badge>}
                          <Badge className="bg-cyan-200 text-cyan-800 text-xs">Donduruldu</Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 rounded-lg text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleUnfreeze(pkg)}
                          data-testid={`pkg-unfreeze-${pkg.mp_id}`}
                          title="Devam Ettir"
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Devam Ettir
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <p className="text-grey">Donma Başlangıcı</p>
                          <p className="font-medium">{pkg.freeze_start ? formatDate(pkg.freeze_start) : "-"}</p>
                        </div>
                        <div>
                          <p className="text-grey">Planlanan Bitiş</p>
                          <p className="font-medium">{pkg.freeze_end_planned ? formatDate(pkg.freeze_end_planned) : "Süresiz"}</p>
                        </div>
                        <div>
                          <p className="text-grey">Kalan Seans</p>
                          <p className="font-medium">{pkg.remaining_sessions ?? "-"}</p>
                        </div>
                        <div>
                          <p className="text-grey">Fiyat</p>
                          <p className="font-medium">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                      </div>
                      {pkg.freeze_reason && (
                        <div className="mt-2 pt-2 border-t border-cyan-200">
                          <p className="text-xs text-grey">Donma sebebi:</p>
                          <p className="text-sm">{pkg.freeze_reason}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
          
          {/* Deleted Packages (Admin/Manager only) */}
          {isPrivileged && deletedPackages.length > 0 && (
            <Card className="border-0 shadow-sm border-l-4 border-l-slate-400">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-600">
                  <Trash2 className="w-5 h-5" />
                  Silinen Paketler ({deletedPackages.length})
                  <Badge className="bg-slate-200 text-slate-700 text-xs ml-2">Sadece yönetici</Badge>
                </CardTitle>
                <p className="text-xs text-grey mt-1">Yumuşak silinmiş paketler. Yönetici/Admin geri yükleyebilir.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {deletedPackages.map((pkg) => {
                  const pkgPrice = pkg.price || pkg.sale_price || 0;
                  return (
                    <div
                      key={pkg.mp_id}
                      className="p-3 rounded-xl border border-slate-300 bg-slate-50 opacity-80"
                      data-testid={`deleted-pkg-${pkg.mp_id}`}
                    >
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-navy">{pkg.package_name || t("packages:packageLabel")}</p>
                          {pkg.package_no && <Badge variant="outline" className="text-xs">{pkg.package_no}</Badge>}
                          <Badge className="bg-slate-200 text-slate-700 text-xs">Silindi</Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 rounded-lg text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => setRestoreConfirm(pkg)}
                          data-testid={`pkg-restore-${pkg.mp_id}`}
                          title="Geri Yükle"
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Geri Yükle
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <p className="text-grey">Fiyat</p>
                          <p className="font-medium">{formatTurkishCurrency(pkgPrice)}</p>
                        </div>
                        <div>
                          <p className="text-grey">Silinme Tarihi</p>
                          <p className="font-medium">{pkg.deleted_at ? formatDate(pkg.deleted_at) : "-"}</p>
                        </div>
                        <div>
                          <p className="text-grey">Başlangıç</p>
                          <p className="font-medium">{formatDate(pkg.start_date || pkg.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-grey">Bitiş</p>
                          <p className="font-medium">{pkg.end_date ? formatDate(pkg.end_date) : "-"}</p>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <p className="text-xs text-grey">Silme nedeni:</p>
                        <p className="text-sm">{pkg.delete_reason || "Belirtilmedi"}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
          
          {packages.length === 0 && (
            <div className="text-center py-12 text-grey">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t("packages:noPackagesYet")}</p>
            </div>
          )}
        </TabsContent>
        
        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {payments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common:date')}</TableHead>
                      <TableHead>{t("packages:package")}</TableHead>
                      <TableHead className="text-right">{t('common:amount')}</TableHead>
                      <TableHead>{t("common:method")}</TableHead>
                      <TableHead>{t('common:note')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.payment_id}>
                        <TableCell>{formatDate(payment.payment_date || payment.created_at)}</TableCell>
                        <TableCell>{payment.package_name || "-"}</TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          {formatTurkishCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {payment.payment_method === "cash" ? t('payments:cash') : 
                             payment.payment_method === "credit_card" ? t("admin:creditCard") : 
                             payment.payment_method === "bank_transfer" ? "Havale" : payment.payment_method}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-grey text-sm max-w-[200px] truncate">
                          {payment.notes || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-grey">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t("common:henuzOdemeBulunmuyor")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Reservations Tab */}
        <TabsContent value="reservations" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              {/* Filtreler */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="flex-1">
                  <Input
                    placeholder={t("common:searchByTrainer", "Antrenör adına göre ara...")}
                    value={resSearchTerm}
                    onChange={(e) => { setResSearchTerm(e.target.value); setResPage(1); }}
                    className="rounded-xl"
                    data-testid="member-reservations-search"
                  />
                </div>
                <Select value={resStatusFilter} onValueChange={(v) => { setResStatusFilter(v); setResPage(1); }}>
                  <SelectTrigger className="w-full sm:w-[200px] rounded-xl" data-testid="member-reservations-status">
                    <SelectValue placeholder={t("common:status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common:all", "Tümü")}</SelectItem>
                    <SelectItem value="completed">{t("common:completed")}</SelectItem>
                    <SelectItem value="approved">{t("admin:approved")}</SelectItem>
                    <SelectItem value="cancelled">{t("common:cancel")}</SelectItem>
                    <SelectItem value="pending">{t("common:pending")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                let filtered = [...reservations];
                if (resSearchTerm) {
                  const q = resSearchTerm.toLowerCase();
                  filtered = filtered.filter(r => (r.trainer_name || "").toLowerCase().includes(q));
                }
                if (resStatusFilter !== "all") {
                  if (resStatusFilter === "approved") {
                    filtered = filtered.filter(r => r.status === "approved" || r.status === "confirmed");
                  } else if (resStatusFilter === "cancelled") {
                    filtered = filtered.filter(r => r.status === "cancelled" || r.status === "cancelled_late" || r.status === "burned");
                  } else if (resStatusFilter === "pending") {
                    filtered = filtered.filter(r => (r.status || "").startsWith("pending") || (r.status || "").startsWith("change_pending"));
                  } else {
                    filtered = filtered.filter(r => r.status === resStatusFilter);
                  }
                }
                const totalPages = Math.max(1, Math.ceil(filtered.length / resPageSize));
                const start = (resPage - 1) * resPageSize;
                const pageItems = filtered.slice(start, start + resPageSize);
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-12 text-grey">
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{reservations.length === 0 ? t("common:henuzRezervasyonBulunmuyor") : t("common:filtrelereUygunDersBulunamadi", "Filtreye uygun kayıt bulunamadı")}</p>
                    </div>
                  );
                }
                return (
                  <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common:date')}</TableHead>
                        <TableHead>{t('common:time')}</TableHead>
                        <TableHead>{t('packages:package', 'Paket')}</TableHead>
                        <TableHead>{t("common:branch")}</TableHead>
                        <TableHead>{t('common:trainer')}</TableHead>
                        <TableHead>{t('common:status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageItems.map((res) => (
                        <React.Fragment key={res.reservation_id}>
                        <TableRow>
                          <TableCell>{formatDate(res.date)}</TableCell>
                          <TableCell>{res.start_time} - {res.end_time}</TableCell>
                          <TableCell>
                            {res.package_no ? (
                              <Badge variant="outline" className="text-xs font-mono">{res.package_no}</Badge>
                            ) : res.payment_type === "card" ? (
                              <span className="text-xs text-grey">Kart</span>
                            ) : res.is_demo || res.payment_type === "demo" ? (
                              <span className="text-xs text-grey">Demo</span>
                            ) : (
                              <span className="text-xs text-grey">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{getLocalizedBranch(res.branch)}</Badge>
                          </TableCell>
                          <TableCell>{res.trainer_name || "-"}</TableCell>
                          <TableCell>
                            {(() => {
                              const statusMap = {
                                approved: { label: t('admin:approved'), className: "bg-green-100 text-green-700" },
                                confirmed: { label: t('admin:approved'), className: "bg-green-100 text-green-700" },
                                completed: { label: t('common:completed'), className: "bg-blue-100 text-blue-700" },
                                cancelled: { label: res.is_late_cancel ? t('reservations:lateCancellation') : t('common:cancel'), className: "" },
                                cancelled_late: { label: t('reservations:lateCancellation'), className: "" },
                                burned: { label: "Yakıldı", className: "bg-red-800 text-white" },
                                pending: { label: t('common:pending'), className: "bg-orange-100 text-orange-700" },
                                pending_member_approval: { label: "Üye Onayı Bekliyor", className: "bg-amber-100 text-amber-700" },
                                pending_business_approval: { label: "İşletme Onayı Bekliyor", className: "bg-amber-100 text-amber-700" },
                                change_pending_member: { label: "Değişiklik - Üye Onayı", className: "bg-purple-100 text-purple-700" },
                                change_pending_business: { label: "Değişiklik - İşletme Onayı", className: "bg-purple-100 text-purple-700" },
                              };
                              const s = statusMap[res.status] || { label: res.status, className: "" };
                              return (
                                <Badge 
                                  variant={res.status?.includes("cancelled") || res.status === "burned" ? "destructive" : "default"}
                                  className={s.className}
                                >
                                  {s.label}
                                </Badge>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                        {res.lesson_note && (
                          <TableRow className="bg-amber-50/50">
                            <TableCell colSpan={6} className="text-xs italic text-amber-900 py-1.5" data-testid={`member-lesson-note-${res.reservation_id}`}>
                              <span className="font-medium not-italic">📝 {t("reservations:lessonNote", "Ders Açıklaması")}:</span> {res.lesson_note}
                            </TableCell>
                          </TableRow>
                        )}
                        {["cancelled", "cancelled_late", "burned"].includes(res.status) && (
                          <TableRow className="bg-red-50/40">
                            <TableCell colSpan={6} className="text-xs italic text-red-700 py-1.5" data-testid={`member-cancel-reason-${res.reservation_id}`}>
                              <span className="font-medium not-italic">İptal Nedeni:</span> {res.cancel_reason || "Belirtilmedi"}
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  {/* Pagination */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t mt-4">
                    <div className="flex items-center gap-3 text-sm text-grey">
                      <span>{t("common:totalRecords", { count: filtered.length })}</span>
                      <div className="flex items-center gap-2">
                        <span>{t("common:show")}</span>
                        <Select value={resPageSize.toString()} onValueChange={(v) => { setResPageSize(Number(v)); setResPage(1); }}>
                          <SelectTrigger className="w-20 h-8 rounded-lg" data-testid="member-reservations-page-size">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="15">15</SelectItem>
                            <SelectItem value="25">25</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled={resPage <= 1} onClick={() => setResPage(p => Math.max(1, p - 1))} data-testid="member-reservations-prev">‹</Button>
                      <span className="text-sm px-2">{resPage} / {totalPages}</span>
                      <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled={resPage >= totalPages} onClick={() => setResPage(p => Math.min(totalPages, p + 1))} data-testid="member-reservations-next">›</Button>
                    </div>
                  </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Edit Modal - Sadece Antrenör Değiştirme */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common:bagliAntrenoruDegistir")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-grey mb-1">{t("common:member")}</p>
              <p className="font-medium text-navy">{member?.name} {member?.surname}</p>
              <p className="text-sm text-grey mt-1">#{member?.member_no}</p>
            </div>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700">
                <p className="text-sm text-amber-700">
                  {t('admin:registeredMemberEditNote')}
                </p>
              </p>
            </div>
            
            <div>
              <Label>{t("common:assignedTrainer")}</Label>
              <Select value={selectedTrainerId} onValueChange={setSelectedTrainerId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_antrenorSecin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                  {trainers.filter(t => t.is_active !== false).map((trainer) => (
                    <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                      {trainer.name} {trainer.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-grey mt-1">
                {t('common:trainerInfoOnlyNote')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="rounded-xl">
              {t('common:cancel')}
            </Button>
            <Button onClick={handleUpdateMember} className="rounded-xl bg-navy">
              {t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Full Edit Modal - All fields for unregistered members */}
      <Dialog open={showFullEditModal} onOpenChange={setShowFullEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {t('common:editMember')}
              {member?.is_app_registered === false && (
                <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50">
                  {t('common:notRegistered')}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Info note */}
            {member?.is_app_registered === false ? (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-700">
                  {t('admin:unregisteredMemberNote')}
                </p>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-700">
                  <strong>{t('common:note')}</strong> {t('admin:registeredMemberNote')}
                </p>
              </div>
            )}
            
            {/* Personal info - only editable for unregistered members */}
            {member?.is_app_registered === false && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('common:firstName')} <span className="text-red-500">*</span></Label>
                    <Input
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>{t('common:surname')}</Label>
                    <Input
                      value={editFormData.surname}
                      onChange={(e) => setEditFormData({...editFormData, surname: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('common:phone')}</Label>
                    <Input
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                      className="rounded-xl"
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                  <div>
                    <Label>{t('common:email')}</Label>
                    <Input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label>{t('members:birthDate')}</Label>
                    <Popover open={detailBirthDateOpen} onOpenChange={setDetailBirthDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal rounded-xl mt-1",
                            !editFormData.birth_date && "text-muted-foreground"
                          )}
                          data-testid="edit-detail-birth-date-trigger"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {editFormData.birth_date ? (
                            format(new Date(editFormData.birth_date), "dd.MM.yyyy", { locale: getDateLocale() })
                          ) : (
                            <span>{t('common:selectDate')}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={editFormData.birth_date ? new Date(editFormData.birth_date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              setEditFormData({
                                ...editFormData,
                                birth_date: format(date, "yyyy-MM-dd")
                              });
                              setDetailBirthDateOpen(false);
                            }
                          }}
                          defaultMonth={editFormData.birth_date ? new Date(editFormData.birth_date) : new Date()}
                          fromYear={1940}
                          toYear={new Date().getFullYear()}
                          initialFocus={false}
                          locale={getDateLocale()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{t('common:gender')}</Label>
                    <Select 
                      value={editFormData.gender || "none"} 
                      onValueChange={(v) => setEditFormData({...editFormData, gender: v === "none" ? "" : v})}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:ph_secin")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("common:secin")}</SelectItem>
                        <SelectItem value="male">{t('common:male')}</SelectItem>
                        <SelectItem value="female">{t('common:female')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
            
            {/* Common fields */}
            <div>
              <Label className="block mb-1.5">{t('members:membershipCard')} (Çoklu Seçilebilir)</Label>
              <div className="flex flex-wrap gap-2 p-2.5 bg-slate-50 border rounded-xl">
                {businessCards.map((card) => {
                  const cardName = typeof card === 'string' ? card : card.name || card.card_name;
                  const isSelected = (editFormData.member_cards || []).includes(cardName);
                  return (
                    <button
                      key={cardName}
                      type="button"
                      onClick={() => {
                        const current = editFormData.member_cards || [];
                        const updated = isSelected
                          ? current.filter(c => c !== cardName)
                          : [...current, cardName];
                        setEditFormData({
                          ...editFormData,
                          member_cards: updated,
                          member_card: updated[0] || ""
                        });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                        isSelected 
                          ? "bg-navy text-white border-navy" 
                          : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
                      }`}
                    >
                      {cardName} {isSelected && "✓"}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div>
              <Label>{t("common:assignedTrainer")}</Label>
              <Select 
                value={editFormData.trainer_id || "none"} 
                onValueChange={(v) => setEditFormData({...editFormData, trainer_id: v})}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_antrenorSecin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                  {trainers.filter(t => t.is_active !== false).map((trainer) => (
                    <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                      {trainer.name} {trainer.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('common:healthNotes')}</Label>
              <Textarea
                value={editFormData.health_notes}
                onChange={(e) => setEditFormData({...editFormData, health_notes: e.target.value})}
                className="rounded-xl"
                placeholder={t("common:ph_uyeHakkindaNotlar")}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFullEditModal(false)} className="rounded-xl">
              {t('common:cancel')}
            </Button>
            <Button 
              onClick={handleFullUpdateMember} 
              className="rounded-xl bg-navy"
              disabled={savingFullEdit}
            >
              {savingFullEdit ? t('common:saving') : t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('payments:receivePayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPackageForPayment && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="font-medium">{selectedPackageForPayment.package_name}</p>
                <p className="text-sm text-grey">
                  {t('packages:remainingDebt')}: <span className="text-red-600 font-bold">
                    {formatTurkishCurrency(selectedPackageForPayment.debt)}
                  </span>
                </p>
              </div>
            )}
            <div>
              <Label>{t('common:amount')}</Label>
              <CurrencyInput
                value={paymentAmount}
                onChange={setPaymentAmount}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>{t('payments:paymentMethod')}</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tahsilat Tarihi</Label>
              <DatePickerTR
                value={paymentDate}
                onChange={(ds) => ds && setPaymentDate(ds)}
                returnDateString
                placeholder="Tarih seç"
                data-testid="member-payment-date"
              />
            </div>
            <div>
              <Label>{t('common:note')}</Label>
              <Textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder={t("common:ph_odemeNotu")}
                className="rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="rounded-xl">
              {t('common:cancel')}
            </Button>
            <Button onClick={handleAddPayment} disabled={savingPayment} className="rounded-xl bg-navy" data-testid="member-payment-save">
              {savingPayment ? "Kaydediliyor..." : t('common:savePayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sell Package Modal */}
      <Dialog open={showSellPackageModal} onOpenChange={(open) => {
        setShowSellPackageModal(open);
        if (!open) {
          // Modal kapandığında tüm state'leri resetle
          setSellPackageId("");
          setSellTrainer("");
          setSellPrice("");
          setSellPaidAmount("0");
          setSellPaymentMethod("cash");
          setSellDiscountReason("");
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('members:packageSale')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Member Info */}
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="font-medium text-navy">{member?.name} {member?.surname}</p>
              <p className="text-sm text-grey">#{member?.member_no}</p>
            </div>
            
            {/* Package Selection */}
            <div>
              <Label>{t('members:selectPackage')}</Label>
              {packagesWithPrice.length > 0 ? (
                <Select value={sellPackageId} onValueChange={(v) => {
                  setSellPackageId(v);
                  const selectedPkg = packagesWithPrice.find(p => p.package_id === v);
                  const priceInfo = activePriceList?.prices?.find(p => p.package_id === v);
                  const price = priceInfo?.price || selectedPkg?.price || 0;
                  setSellPrice(String(price));
                  setSellTrainer("");  // Reset trainer when package changes
                }}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("packages:ph_paketSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {packagesWithPrice.map((pkg) => {
                      const priceInfo = activePriceList?.prices?.find(p => p.package_id === pkg.package_id);
                      return (
                        <SelectItem key={pkg.package_id} value={pkg.package_id}>
                          {pkg.name} - {currencySymbol}{priceInfo?.price || pkg.price || 0}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  {t('common:priceListNotDefined')}
                </div>
              )}
            </div>
            
            {sellPackageId && (
              <>
                <div>
                  <Label>{t('common:salePrice')} ({currencySymbol})</Label>
                  <Input
                    type="number"
                    value={sellPrice}
                    onChange={(e) => setSellPrice(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                
                <div>
                  <Label>Satış Tarihi</Label>
                  <DatePickerTR
                    value={sellSaleDate}
                    onChange={(ds) => ds && setSellSaleDate(ds)}
                    returnDateString
                    placeholder="Tarih seç"
                    data-testid="sell-package-sale-date"
                  />
                  <p className="text-xs text-grey mt-1">Geriye dönük tarih de seçebilirsiniz.</p>
                </div>
                
                {canTransferPackage && (
                  <TransferPackageFields
                    enabled={sellIsTransfer}
                    onToggle={setSellIsTransfer}
                    usedSessions={sellTransferUsed}
                    onUsedChange={setSellTransferUsed}
                    endDate={sellTransferEnd}
                    onEndDateChange={setSellTransferEnd}
                    totalSessions={packagesWithPrice.find(p => p.package_id === sellPackageId)?.session_count || 0}
                    testIdSuffix="-detail"
                  />
                )}
                
                <div>
                  <Label>{sellIsTransfer ? "Daha önce ödenen tutar" : t('common:paidAmountLabel')} ({currencySymbol})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={sellPaidAmount}
                    onChange={(e) => setSellPaidAmount(e.target.value)}
                    className="rounded-xl"
                    data-testid="sell-paid-amount-detail"
                  />
                  {sellIsTransfer && <p className="text-xs text-violet-700 mt-1">Gelir raporlarına girmez.</p>}
                  {sellPrice && parseInt(sellPaidAmount || 0) < parseInt(sellPrice) && (
                    <p className="text-xs text-amber-600 mt-1">
                      {t('packages:remainingAmount')}: {currencySymbol}{(parseInt(sellPrice) - parseInt(sellPaidAmount || 0)).toLocaleString('tr-TR')}
                    </p>
                  )}
                </div>
                
                {/* Ödeme yöntemi - sadece ödeme varsa göster */}
                {!sellIsTransfer && parseInt(sellPaidAmount || 0) > 0 && (
                  <div>
                    <Label>{t('payments:paymentMethod')}</Label>
                    <Select value={sellPaymentMethod} onValueChange={setSellPaymentMethod}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:selectPaymentMethod")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                        <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                        <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label>{t('common:trainerOptional')}</Label>
                  <Select value={sellTrainer || "none"} onValueChange={(v) => setSellTrainer(v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:selectTrainer")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                      {(() => {
                        // Filter trainers by selected package's branch
                        const selectedPkg = packagesWithPrice.find(p => p.package_id === sellPackageId);
                        const pkgBranches = selectedPkg?.branches || [];
                        return trainers
                          .filter(t => t.is_active !== false)
                          .filter(t => {
                            if (pkgBranches.length === 0) return true;
                            const trainerBranches = t.branches || [];
                            return pkgBranches.some(b => trainerBranches.includes(b));
                          })
                          .map((trainer) => (
                            <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                              {trainer.name} {trainer.surname}
                            </SelectItem>
                          ));
                      })()}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const selectedPkg = packagesWithPrice.find(p => p.package_id === sellPackageId);
                    const pkgBranches = selectedPkg?.branches || [];
                    if (pkgBranches.length > 0) {
                      return (
                        <p className="text-xs text-blue-600 mt-1">
                          {t('common:onlyBranchTrainersShown', { branches: pkgBranches.join(", ") })}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                
                <div>
                  <Label>{t('common:discountNote')}</Label>
                  <Textarea
                    value={sellDiscountReason}
                    onChange={(e) => setSellDiscountReason(e.target.value)}
                    placeholder={t("common:ph_i̇ndirimNedeniVeyaNot")}
                    className="rounded-xl"
                    rows={2}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSellPackageModal(false)} className="rounded-xl">
              {t('common:cancel')}
            </Button>
            <Button 
              onClick={handleSellPackage} 
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
              disabled={!sellPackageId || isSelling}
            >
              {isSelling ? t('common:selling') : t('common:completeSale')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Package Action Modal (Deactivate / Cancel / Delete with reason) */}
      <Dialog open={!!packageActionPkg} onOpenChange={(open) => !open && closePackageAction()}>
        <DialogContent className="max-w-sm" data-testid="package-action-modal">
          <DialogHeader>
            <DialogTitle>
              {packageActionPkg?.action === "deactivate" && "Paketi Pasife Al"}
              {packageActionPkg?.action === "cancel" && "Paketi İptal Et"}
              {packageActionPkg?.action === "delete" && "Paketi Sil"}
            </DialogTitle>
          </DialogHeader>
          {packageActionPkg && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-grey">Paket</p>
                <p className="font-medium text-navy">
                  {packageActionPkg.pkg?.package_name || t("packages:packageLabel")}
                  {packageActionPkg.pkg?.package_no ? ` · ${packageActionPkg.pkg.package_no}` : ""}
                </p>
                {packageActionInfo && (
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    <div>
                      <p className="text-grey">Ödenen</p>
                      <p className={`font-bold ${packageActionInfo.paidAmount > 0 ? "text-green-700" : "text-grey"}`}>
                        {formatTurkishCurrency(packageActionInfo.paidAmount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-grey">Yapılan Ders</p>
                      <p className={`font-bold ${packageActionInfo.usedSessions > 0 ? "text-blue-700" : "text-grey"}`}>
                        {packageActionInfo.usedSessions} / {packageActionInfo.totalSessions}
                      </p>
                    </div>
                    <div>
                      <p className="text-grey">Borç</p>
                      <p className={`font-bold ${packageActionInfo.debt > 0 ? "text-red-700" : "text-grey"}`}>
                        {formatTurkishCurrency(packageActionInfo.debt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* DELETE: pre-check warnings */}
              {packageActionPkg.action === "delete" && packageActionInfo && (
                (packageActionInfo.paidAmount > 0 || packageActionInfo.usedSessions > 0) ? (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm space-y-1">
                    <p className="font-semibold text-red-700">⚠️ Bu paket SİLİNEMEZ</p>
                    {packageActionInfo.paidAmount > 0 && (
                      <p className="text-red-700">• Bu pakete <b>{formatTurkishCurrency(packageActionInfo.paidAmount)}</b> ödeme yapılmış.</p>
                    )}
                    {packageActionInfo.usedSessions > 0 && (
                      <p className="text-red-700">• Bu pakete bağlı <b>{packageActionInfo.usedSessions}</b> tamamlanmış ders var (antrenör primleri etkilenir).</p>
                    )}
                    <p className="text-red-800 mt-2 font-medium">
                      Lütfen <b>&quot;İptal Et&quot;</b> seçeneğini kullanın — geçmiş kayıtlar korunarak paket sonlandırılır.
                    </p>
                  </div>
                ) : (
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
                    <p className="text-emerald-800">
                      ✓ Bu pakette hiç ödeme veya ders kaydı yok. Güvenle silinebilir.
                    </p>
                  </div>
                )
              )}

              {/* CANCEL BÖLÜMÜ: 1. Ödeme Durumu (Ödenen > 0 ise) */}
              {packageActionPkg.action === "cancel" && packageActionInfo?.paidAmount > 0 && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
                  <p className="text-sm font-semibold text-amber-900">
                    💰 Bu pakete {formatTurkishCurrency(packageActionInfo.paidAmount)} ödeme yapılmış. İade yapılacak mı?
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="cancel-refund-mode"
                        checked={cancelRefundMode === "free"}
                        onChange={() => setCancelRefundMode("free")}
                        className="mt-1"
                        data-testid="cancel-mode-free"
                      />
                      <div className="text-sm">
                        <p className="font-medium text-navy">İade Yapılmayacak (Ödeme kasada kalsın)</p>
                        <p className="text-xs text-grey">Ödenen tutar firma kasasında kalır, raporlara gider yazılmaz.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="cancel-refund-mode"
                        checked={cancelRefundMode === "refund"}
                        onChange={() => setCancelRefundMode("refund")}
                        className="mt-1"
                        data-testid="cancel-mode-refund"
                      />
                      <div className="text-sm flex-1">
                        <p className="font-medium text-navy">İadeli İptal (Kasadan para çıkışı yap)</p>
                        <p className="text-xs text-grey">Belirtilen tutar iade edilir ve kasa raporlarına gider işlenir.</p>
                        {cancelRefundMode === "refund" && (
                          <div className="mt-1.5">
                            <Input
                              type="number"
                              value={cancelRefundAmount}
                              onChange={(e) => setCancelRefundAmount(e.target.value)}
                              placeholder="İade tutarı"
                              className="rounded-lg h-8 bg-white"
                              max={packageActionInfo.paidAmount}
                              min="0"
                              data-testid="cancel-refund-amount"
                            />
                            <p className="text-xs text-grey mt-0.5">
                              Maksimum iade: {formatTurkishCurrency(packageActionInfo.paidAmount)}
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* CANCEL BÖLÜMÜ: 2. Borç Durumu (Borç > 0 ise) */}
              {packageActionPkg.action === "cancel" && packageActionInfo?.debt > 0 && (
                <div className="p-3 rounded-xl bg-orange-50 border border-orange-200 space-y-2">
                  <p className="text-sm font-semibold text-orange-950">
                    ⚠️ Bu pakete ait {formatTurkishCurrency(packageActionInfo.debt)} kalan borç var. Borç ne yapılacak?
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="cancel-debt-mode"
                        checked={cancelDebtMode === "waive_debt"}
                        onChange={() => setCancelDebtMode("waive_debt")}
                        className="mt-1"
                      />
                      <div className="text-sm">
                        <p className="font-medium text-navy">Borcu Sil (Ücretsiz İptal)</p>
                        <p className="text-xs text-grey">Üyeden para talep edilmeyecek. Kalan borç hem üye profilinden hem kasa raporlarından silinir.</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="cancel-debt-mode"
                        checked={cancelDebtMode === "keep_debt"}
                        onChange={() => setCancelDebtMode("keep_debt")}
                        className="mt-1"
                      />
                      <div className="text-sm flex-1">
                        <p className="font-medium text-navy">Borç Kalsın (Ücretli İptal / Ceza)</p>
                        <p className="text-xs text-grey">Paket iptal edilir ancak belirlediğiniz borç üyenin profilinde ve bekleyen borç raporunda kalır.</p>
                        
                        {/* KISMİ BORÇ BELİRLEME KUTUSU */}
                        {cancelDebtMode === "keep_debt" && (
                          <div className="mt-1.5">
                            <Input
                              type="number"
                              value={cancelDebtAmount}
                              onChange={(e) => setCancelDebtAmount(e.target.value)}
                              placeholder="Tahsil edilecek borç tutarı"
                              className="rounded-lg h-8 bg-white max-w-[200px]"
                              max={packageActionInfo.debt}
                              min="0"
                            />
                            <p className="text-[11px] text-grey mt-0.5">
                              Maksimum borç: {formatTurkishCurrency(packageActionInfo.debt)} (Dilerseniz daha az yazarak kısmi silebilirsiniz)
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* CANCEL BİLGİLENDİRME KUTUSU */}
              {packageActionPkg.action === "cancel" && (
                <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 space-y-1">
                  <p>• Geçmiş tamamlanmış dersler korunur (antrenör primleri etkilenmez).</p>
                  <p>• Bu pakete bağlı açık rezervasyonlar otomatik iptal edilir.</p>
                </div>
              )}

              <div>
                <Label>
                  {packageActionPkg.action === "deactivate" && "Pasife alma nedeni"}
                  {packageActionPkg.action === "cancel" && "İptal nedeni"}
                  {packageActionPkg.action === "delete" && "Silme nedeni"}
                  <span className="text-red-500"> *</span>
                </Label>
                <Textarea
                  value={packageActionReason}
                  onChange={(e) => setPackageActionReason(e.target.value)}
                  placeholder="Nedeni kısaca açıklayın..."
                  rows={3}
                  className="rounded-xl"
                  data-testid="package-action-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closePackageAction}
              className="rounded-xl"
              disabled={packageActionLoading}
            >
              {t('common:cancel')}
            </Button>
            <Button
              onClick={submitPackageAction}
              disabled={
                !packageActionReason.trim() ||
                packageActionLoading ||
                (packageActionPkg?.action === "delete" && packageActionInfo &&
                  (packageActionInfo.paidAmount > 0 || packageActionInfo.usedSessions > 0))
              }
              className={`rounded-xl text-white ${
                packageActionPkg?.action === "delete"
                  ? "bg-red-600 hover:bg-red-700"
                  : packageActionPkg?.action === "cancel"
                  ? "bg-orange-600 hover:bg-orange-700"
                  : "bg-slate-600 hover:bg-slate-700"
              }`}
              data-testid="package-action-submit"
            >
              {packageActionLoading ? "Kaydediliyor..." : "Onayla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Edit Sale Modal (price + package template) */}
      <Dialog open={!!editSalePkg} onOpenChange={(open) => !open && setEditSalePkg(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-sale-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Pencil className="w-5 h-5" />
              Paket Düzenle
            </DialogTitle>
          </DialogHeader>
          {editSalePkg && (() => {
            const usedSessions = Number(editSalePkg.used_sessions || 0);
            const priceInfo = activePriceList?.prices?.find(p => p.package_id === editSalePackageId);
            const cands = [priceInfo?.price, priceInfo?.discounted_price]
              .map(Number).filter(v => v && v > 0);
            const lo = cands.length ? Math.min(...cands) : null;
            const hi = cands.length ? Math.max(...cands) : null;
            const priceNum = parseFloat(editSalePrice);
            const priceOutOfRange = !isPrivileged && lo != null && hi != null &&
              !isNaN(priceNum) && (priceNum < lo || priceNum > hi);
            return (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-grey">Mevcut Paket</p>
                <p className="font-medium text-navy">
                  {editSalePkg.package_name}
                  {editSalePkg.package_no ? ` · ${editSalePkg.package_no}` : ""}
                </p>
                <p className="text-xs text-grey mt-1">Yapılan ders: <span className="font-semibold text-navy">{usedSessions}</span></p>
              </div>

              {usedSessions > 0 && (
                <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  ⚠️ Bu üye {usedSessions} ders yaptı. Sadece {usedSessions} seans veya daha fazla derslikli paketler seçilebilir.
                </div>
              )}

              <div>
                <Label>Paket Türü</Label>
                <select
                  value={editSalePackageId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditSalePackageId(v);
                    // Suggest the list price for the newly selected package
                    const pi = activePriceList?.prices?.find(p => p.package_id === v);
                    if (pi?.price) setEditSalePrice(String(pi.price));
                  }}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  data-testid="edit-sale-package-select"
                >
                  {availablePackages.map(p => {
                    const sc = Number(p.session_count || 0);
                    const disabled = sc < usedSessions;
                    return (
                      <option key={p.package_id} value={p.package_id} disabled={disabled}>
                        {p.name} ({p.session_count} seans){disabled ? " — yetersiz" : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <Label>Satış Fiyatı ({currencySymbol})</Label>
                <Input
                  type="number"
                  value={editSalePrice}
                  onChange={(e) => setEditSalePrice(e.target.value)}
                  className={`rounded-xl ${priceOutOfRange ? "border-red-400" : ""}`}
                  data-testid="edit-sale-price-input"
                />
                {!isPrivileged && lo != null && hi != null && (
                  <p className={`text-xs mt-1 ${priceOutOfRange ? "text-red-600 font-medium" : "text-grey"}`}>
                    İzin verilen aralık: {currencySymbol}{lo.toLocaleString('tr-TR')} - {currencySymbol}{hi.toLocaleString('tr-TR')}
                    {priceOutOfRange ? " — bu aralık dışı için yönetici yetkisi gerekir." : ""}
                  </p>
                )}
                {isPrivileged && (
                  <p className="text-xs text-grey mt-1">Yönetici olarak istediğiniz fiyatı girebilirsiniz.</p>
                )}
              </div>
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSalePkg(null)} className="rounded-xl" disabled={editSaleLoading}>
              Vazgeç
            </Button>
            <Button
              onClick={submitEditSale}
              disabled={editSaleLoading}
              className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="edit-sale-submit"
            >
              <Pencil className="w-4 h-4 mr-1" />
              {editSaleLoading ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freeze Package Modal */}
      <Dialog open={!!freezeModalPkg} onOpenChange={(open) => !open && setFreezeModalPkg(null)}>
        <DialogContent className="max-w-sm" data-testid="freeze-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-700">
              <Pause className="w-5 h-5" />
              Paketi Dondur
            </DialogTitle>
          </DialogHeader>
          {freezeModalPkg && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-grey">Paket</p>
                <p className="font-medium text-navy">
                  {freezeModalPkg.package_name || t("packages:packageLabel")}
                  {freezeModalPkg.package_no ? ` · ${freezeModalPkg.package_no}` : ""}
                </p>
              </div>
              
              <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                ⚠️ Donma süresince üye bu paketle rezervasyon oluşturamaz. Donma aralığında kalan açık rezervasyonlar otomatik iptal edilir. Süre bitiş tarihine eklenir.
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Başlangıç *</Label>
                  <DateInputTR value={freezeStartDate}
          onChange={(e) => setFreezeStartDate(e.target.value)}
          className="rounded-xl"
          data-testid="freeze-start-date" />
                </div>
                <div>
                  <Label>Bitiş (opsiyonel)</Label>
                  <DateInputTR value={freezeEndDate}
          onChange={(e) => setFreezeEndDate(e.target.value)}
          className="rounded-xl"
          data-testid="freeze-end-date" />
                </div>
              </div>
              <p className="text-xs text-grey">Bitiş boş bırakılırsa süresiz dondurulur — istediğinizde &quot;Devam Ettir&quot; ile aktive edilir.</p>
              
              <div>
                <Label>Donma Sebebi *</Label>
                <Textarea
                  value={freezeReason}
                  onChange={(e) => setFreezeReason(e.target.value)}
                  placeholder="Üye tatile gidiyor / hastalık / ..."
                  rows={2}
                  className="rounded-xl"
                  data-testid="freeze-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreezeModalPkg(null)} className="rounded-xl" disabled={freezeLoading}>
              Vazgeç
            </Button>
            <Button
              onClick={submitFreeze}
              disabled={!freezeReason.trim() || !freezeStartDate || freezeLoading}
              className="rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="freeze-submit"
            >
              <Pause className="w-4 h-4 mr-1" />
              {freezeLoading ? "Donduruluyor..." : "Dondur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Package Confirmation Dialog (admin/manager only) */}
      <Dialog open={!!restoreConfirm} onOpenChange={(open) => !open && setRestoreConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="restore-confirm-modal">
          <DialogHeader>
            <DialogTitle>Paketi Geri Yükle</DialogTitle>
          </DialogHeader>
          {restoreConfirm && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-grey">Paket</p>
                <p className="font-medium text-navy">
                  {restoreConfirm.package_name || t("packages:packageLabel")}
                  {restoreConfirm.package_no ? ` · ${restoreConfirm.package_no}` : ""}
                </p>
              </div>
              <p className="text-sm text-navy">
                Bu paketi <b>aktif</b> duruma geri yüklemek istediğinizden emin misiniz?
              </p>
              <p className="text-xs text-grey">
                Paket &quot;Devam Eden Paketler&quot; listesine geri eklenecek. Silme bilgisi (tarih, sebep) temizlenecek.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestoreConfirm(null)}
              className="rounded-xl"
            >
              Vazgeç
            </Button>
            <Button
              onClick={() => handleRestorePackage(restoreConfirm)}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="restore-confirm-submit"
            >
              <Play className="w-4 h-4 mr-1" />
              Geri Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Edit Package Dates Modal */}
      <Dialog open={!!editingDatesPkg} onOpenChange={(open) => !open && setEditingDatesPkg(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-package-dates-modal">
          <DialogHeader>
            <DialogTitle>{t("members:editPackageDates", "Paket Tarihlerini Düzenle")}</DialogTitle>
          </DialogHeader>
          {editingDatesPkg && (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-grey">
                {t("members:editPackageDatesHint", "Başlangıç değiştirilince bitiş paket geçerlilik süresine göre otomatik hesaplanır.")}
              </p>
              <div>
                <Label className="text-xs">{t("settings:startTime")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-xl"
                      data-testid="edit-package-start-date"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {editingDatesPkg.start_date
                        ? format(new Date(editingDatesPkg.start_date), "dd.MM.yyyy", { locale: getDateLocale() })
                        : <span className="text-grey">{t('common:selectDate', 'Tarih seç')}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={editingDatesPkg.start_date ? new Date(editingDatesPkg.start_date) : undefined}
                      onSelect={(date) => {
                        if (date) handleEditStartDate(format(date, "yyyy-MM-dd"));
                      }}
                      defaultMonth={editingDatesPkg.start_date ? new Date(editingDatesPkg.start_date) : new Date()}
                      locale={getDateLocale()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs">{t("settings:endTime")}</Label>
                <DateInputTR
                  value={editingDatesPkg.end_date}
                  onChange={(e) => setEditingDatesPkg({ ...editingDatesPkg, end_date: e.target.value })}
                  className="rounded-xl w-full"
                  data-testid="edit-package-end-date"
                />
                <p className="text-[11px] text-grey mt-1">
                  {t("members:autoCalcNote", "Geçerlilik süresi: {{n}} gün", { n: editingDatesPkg.validity_days })}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditingDatesPkg(null)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button
              onClick={saveEditDates}
              disabled={savingDates}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="save-package-dates-btn"
            >
              {savingDates ? t("common:saving", "Kaydediliyor...") : t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Number Modal */}
      <Dialog open={!!editingMemberNo} onOpenChange={(open) => !open && setEditingMemberNo(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-member-no-modal">
          <DialogHeader>
            <DialogTitle>{t("members:editMemberNo", "Üye Numarasını Düzenle")}</DialogTitle>
          </DialogHeader>
          {editingMemberNo && (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-grey">
                {t("members:editMemberNoHint", "Bu numara işletmeniz içinde başka bir üyede kullanılıyorsa kaydedilemez.")}
              </p>
              <Input
                value={editingMemberNo.value}
                onChange={(e) => setEditingMemberNo({ ...editingMemberNo, value: e.target.value })}
                placeholder="UYE-0001"
                maxLength={40}
                className="rounded-xl"
                data-testid="member-no-input"
                onKeyDown={(e) => { if (e.key === "Enter") saveMemberNumber(); }}
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditingMemberNo(null)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button
              onClick={saveMemberNumber}
              disabled={savingNumber}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="save-member-no-btn"
            >
              {savingNumber ? t("common:saving", "Kaydediliyor...") : t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Package Number Modal */}
      <Dialog open={!!editingPackageNo} onOpenChange={(open) => !open && setEditingPackageNo(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-package-no-modal">
          <DialogHeader>
            <DialogTitle>{t("members:editPackageNo", "Paket Numarasını Düzenle")}</DialogTitle>
          </DialogHeader>
          {editingPackageNo && (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-grey">
                {t("members:editPackageNoHint", "Bu numara işletmeniz içinde başka bir pakette kullanılıyorsa kaydedilemez.")}
              </p>
              <Input
                value={editingPackageNo.value}
                onChange={(e) => setEditingPackageNo({ ...editingPackageNo, value: e.target.value })}
                placeholder="PKT-0001"
                maxLength={40}
                className="rounded-xl"
                data-testid="package-no-input"
                onKeyDown={(e) => { if (e.key === "Enter") savePackageNumber(); }}
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditingPackageNo(null)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button
              onClick={savePackageNumber}
              disabled={savingNumber}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="save-package-no-btn"
            >
              {savingNumber ? t("common:saving", "Kaydediliyor...") : t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
